/**
 * The optional collector account.
 *
 * Two rules from the packet decide everything on this page.
 *
 * **The tier is server-verified and read-only.** `checkoutIdentity` in core
 * reads `customer_tier_assignments`, caps the discount at 5000 bps, and applies
 * it when it prices a quote. Nothing the browser sends influences that. So this
 * view *displays* a tier the server injected into the page configuration and
 * offers no control that could set, request, or upgrade one — there is no
 * self-assignment path to secure because there is no path.
 *
 * **This surface cannot sign anyone in.** ADR-003 keeps credentials out of the
 * browser bundle: core authenticates the `customers` collection and answers
 * `POST /api/core/customer/sessions` with a CSRF token while setting a Secure,
 * HttpOnly cookie. This page can therefore *adopt* an existing session and
 * nothing else, which is why it offers a check rather than a login form.
 */

import { type ReactNode, useState } from "react";
import { useStorefront } from "../app/StorefrontContext.js";
import { useAnnounce } from "../components/Announcer.js";
import { ActionButton, DataPlate, PlateGrid, StatusChip } from "../components/primitives.js";
import { openCustomerSession } from "../lib/commands.js";

type Check =
  | { readonly kind: "idle" }
  | { readonly kind: "checking" }
  | { readonly kind: "signed-in" }
  | { readonly kind: "signed-out"; readonly detail: string };

export function AccountView(): ReactNode {
  const { config, transport, csrfToken, setCsrfToken } = useStorefront();
  const announce = useAnnounce();
  const [check, setCheck] = useState<Check>({ kind: "idle" });

  const signedIn = csrfToken !== null || check.kind === "signed-in";
  const verifiedTier = signedIn ? config.collector_tier : null;

  return (
    <div className="stack account">
      <h1 className="t-display-l">Your account</h1>

      {config.account_enabled ? null : (
        <div className="banner banner--accent">
          <StatusChip label="Accounts are off" tone="accent" />
          <span>
            Collector accounts are not switched on for this site yet. Guest checkout works exactly
            the same way, and no benefit is withheld from a guest.
          </span>
        </div>
      )}

      <p className="t-body-l">
        An account holds your order history and saves you re-entering a delivery address. It never
        changes a price by itself: any collector tier is assigned by us and applied by the server
        from its own records.
      </p>

      <section aria-labelledby="tier-title" className="stack">
        <h2 className="t-display-xs" id="tier-title">
          Collector tier
        </h2>
        <PlateGrid>
          <DataPlate
            label="Your tier"
            note="Assigned by the shop and verified by the server on every order."
          >
            {verifiedTier === null ? (
              <span className="t-body-m t-secondary">
                {signedIn
                  ? "No tier is assigned to this session. Standard prices apply."
                  : "Sign in before this page can show a server-verified tier. Standard prices apply."}
              </span>
            ) : (
              <span className="t-body-m">{verifiedTier.label}</span>
            )}
          </DataPlate>
        </PlateGrid>
        <p className="t-body-s t-secondary">
          There is no control on this page that sets or requests a tier. If there were, it would be a
          price a browser could choose, and prices are not a browser&rsquo;s to choose.
        </p>
      </section>

      <section aria-labelledby="session-title" className="stack">
        <h2 className="t-display-xs" id="session-title">
          Sign-in
        </h2>
        {signedIn ? (
          <p className="t-body-m" role="status">
            <StatusChip label="Signed in" tone="ok" /> This browser has an active collector session.
            Checkout will use the email address on your account and will not ask for it again.
          </p>
        ) : (
          <>
            <p className="t-body-m">
              Signing in happens on the shop&rsquo;s own sign-in page, not here — this page never
              handles a password. Once you have signed in there, this button adopts the session for
              checkout.
            </p>
            {check.kind === "signed-out" ? (
              <p className="field-problem" role="status">
                {check.detail}
              </p>
            ) : null}
          </>
        )}

        {signedIn || !config.account_enabled ? null : (
          <ActionButton
            id="adopt-session"
            onClick={() => {
              setCheck({ kind: "checking" });
              void openCustomerSession(transport).then((outcome) => {
                if (outcome.kind === "ok") {
                  setCsrfToken(outcome.value.csrf_token);
                  setCheck({ kind: "signed-in" });
                  announce("Signed in. Checkout will use your account.");
                  return;
                }
                const detail =
                  outcome.kind === "denied"
                    ? "This browser is not signed in to a collector account. Guest checkout works without one."
                    : outcome.kind === "offline"
                      ? "The connection dropped before the server answered."
                      : "The server did not open a session for this browser.";
                setCheck({ kind: "signed-out", detail });
                announce(detail);
              });
            }}
            pending={check.kind === "checking"}
            pendingLabel="Checking…"
            variant="secondary"
          >
            Check for a signed-in account
          </ActionButton>
        )}
      </section>
    </div>
  );
}
